/**
 * Session-token storage for the SPA. The bearer token minted by `/auth/*` is kept
 * in localStorage (survives reload) and attached as `Authorization: Bearer` by the
 * API client. A 401 from any call clears it (see client.ts) so an expired session
 * bounces cleanly back to the sign-in shell.
 */

const KEY = "treillage.admin.session";

export function getToken(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    window.localStorage.setItem(KEY, token);
  } catch {
    /* private-mode / storage-disabled: session simply won't persist across reload */
  }
}

export function clearToken(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Capture a session handed back on the URL by the SSO callback redirect
 * (`?session=<token>`), store it, and strip it from the address bar so the token
 * doesn't linger in history. Returns true if a token was captured.
 *
 * NOTE: the current `/auth/callback` only redirects to RFC-8252 *loopback*
 * targets (built for the native add-in). Handing a session back to a hosted web
 * origin needs a server-side redirect allow-list — tracked as a follow-up. This
 * capture is here so the web SSO round-trip lights up the moment the server
 * allows the admin origin.
 */
export function captureSessionFromUrl(): boolean {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("session");
    if (!token) return false;
    setToken(token);
    url.searchParams.delete("session");
    // The SSO return no longer carries the invite, but strip a stale one defensively
    // so a redeemed link doesn't linger in the address bar / history.
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", url.toString());
    return true;
  } catch {
    return false;
  }
}

/**
 * The invite-link secret on the current URL (`?invite=<token>`), if any (TRE-1044).
 * An invite link opens the SPA with this param; the sign-in shell threads it into
 * the chosen login flow so the invitee is admitted + bound to the invite's account
 * and role in one round-trip. Returns null when no invite is present.
 */
export function getInviteToken(): string | null {
  try {
    return new URL(window.location.href).searchParams.get("invite");
  } catch {
    return null;
  }
}

/** Strip `?invite=` from the address bar once it's been consumed by a login. */
export function clearInviteParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("invite")) return;
    url.searchParams.delete("invite");
    window.history.replaceState({}, "", url.toString());
  } catch {
    /* ignore */
  }
}
